import { useCallback, useEffect, useRef, useState } from "react";

/**
 * InteractiveImageViewer - A reusable component for viewing images with zoom and pan functionality
 *
 * Props:
 * - imageSrc: string (required) - The image source URL or base64 data
 * - imageAlt: string (optional) - Alt text for the image
 * - containerHeight: string (optional) - CSS height value, defaults to dynamic calculation
 * - showControls: boolean (optional) - Whether to show zoom/pan controls, defaults to true
 * - onImageLoad: function (optional) - Callback when image loads
 * - className: string (optional) - Additional CSS class for the container
 */
const InteractiveImageViewer = ({
  imageSrc,
  imageAlt = "Interactive image",
  containerHeight,
  showControls = true,
  backgroundColor = "#f8f9fa",
  onImageLoad,
  className = "",
  ...props
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
  const [viewportHeight, setViewportHeight] = useState(
    containerHeight || "calc(100vh - 130px)",
  );

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Reset to original state
  const resetTransform = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Handle zoom with mouse wheel
  const handleWheel = useCallback(
    (e) => {
      if (!containerRef.current) return;

      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      const newScale = Math.min(Math.max(0.1, scale * zoomFactor), 5);

      // Calculate new position to zoom towards mouse cursor
      const scaleChange = newScale / scale;
      const newX = mouseX - (mouseX - position.x) * scaleChange;
      const newY = mouseY - (mouseY - position.y) * scaleChange;

      setScale(newScale);
      setPosition({ x: newX, y: newY });
    },
    [scale, position],
  );

  // Handle mouse down for dragging
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      // Left mouse button
      setIsDragging(true);
      setLastMousePosition({ x: e.clientX, y: e.clientY });
    }
  }, []);

  // Handle mouse move for dragging
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const deltaX = e.clientX - lastMousePosition.x;
      const deltaY = e.clientY - lastMousePosition.y;

      setPosition((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));

      setLastMousePosition({ x: e.clientX, y: e.clientY });
    },
    [isDragging, lastMousePosition],
  );

  // Handle mouse up to stop dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle touch events for mobile
  const [touches, setTouches] = useState([]);
  const [initialDistance, setInitialDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(1);

  const getTouchDistance = (touch1, touch2) => {
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2),
    );
  };

  const handleTouchStart = useCallback(
    (e) => {
      const touchList = Array.from(e.touches);
      setTouches(touchList);

      if (touchList.length === 2) {
        const distance = getTouchDistance(touchList[0], touchList[1]);
        setInitialDistance(distance);
        setInitialScale(scale);
      } else if (touchList.length === 1) {
        setIsDragging(true);
        setLastMousePosition({
          x: touchList[0].clientX,
          y: touchList[0].clientY,
        });
      }
    },
    [scale],
  );

  const handleTouchMove = useCallback(
    (e) => {
      e.preventDefault();
      const touchList = Array.from(e.touches);

      if (touchList.length === 2) {
        // Pinch to zoom
        const distance = getTouchDistance(touchList[0], touchList[1]);
        const newScale = Math.min(
          Math.max(0.1, initialScale * (distance / initialDistance)),
          5,
        );
        setScale(newScale);
      } else if (touchList.length === 1 && isDragging) {
        // Pan
        const deltaX = touchList[0].clientX - lastMousePosition.x;
        const deltaY = touchList[0].clientY - lastMousePosition.y;

        setPosition((prev) => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));

        setLastMousePosition({
          x: touchList[0].clientX,
          y: touchList[0].clientY,
        });
      }
    },
    [isDragging, lastMousePosition, initialDistance, initialScale],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setTouches([]);
  }, []);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "r" || e.key === "R") {
        resetTransform();
      } else if (e.key === "+" || e.key === "=") {
        setScale((prev) => Math.min(prev * 1.1, 5));
      } else if (e.key === "-") {
        setScale((prev) => Math.max(prev * 0.9, 0.1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [resetTransform]);

  // Add mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Dynamically calculate available height by detecting navbar elements
  useEffect(() => {
    if (containerHeight) {
      setViewportHeight(containerHeight);
      return;
    }

    const calculateViewportHeight = () => {
      const navbar = document.querySelector(".navbar-bym.navbar");
      const subNav = document.querySelector("ul.navigation-bar");

      let totalNavbarHeight = 0;

      if (navbar) {
        totalNavbarHeight += navbar.offsetHeight;
      }
      if (subNav) {
        totalNavbarHeight += subNav.offsetHeight;
      }

      // Fallback to estimated height if elements not found
      if (totalNavbarHeight === 0) {
        totalNavbarHeight = 130; // Estimated total height
      }

      // Add some padding to prevent clipping
      const padding = 10;
      setViewportHeight(`calc(100vh - ${totalNavbarHeight + padding}px)`);
    };

    // Calculate on mount and resize
    calculateViewportHeight();
    window.addEventListener("resize", calculateViewportHeight);

    return () => window.removeEventListener("resize", calculateViewportHeight);
  }, [containerHeight]);

  // Reset transform when image source changes
  useEffect(() => {
    resetTransform();
    if (onImageLoad && imageSrc) {
      onImageLoad();
    }
  }, [imageSrc, resetTransform, onImageLoad]);

  if (!imageSrc) {
    return null;
  }

  return (
    <div
      className={`interactive-image-viewer ${className}`}
      style={{
        position: "relative",
        width: "100%",
        height: viewportHeight,
        minHeight: "400px",
        overflow: "hidden",
        margin: "0",
        padding: "0",
        backgroundColor: backgroundColor,
      }}
      {...props}
    >
      {/* Controls */}
      {showControls && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          <button
            onClick={resetTransform}
            style={{
              padding: "8px 12px",
              backgroundColor: "#ffffff",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
            title="Reset view (R)"
          >
            Reset
          </button>
          <button
            onClick={() => setScale((prev) => Math.min(prev * 1.2, 5))}
            style={{
              padding: "8px 12px",
              backgroundColor: "#ffffff",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
            title="Zoom in (+)"
          >
            +
          </button>
          <button
            onClick={() => setScale((prev) => Math.max(prev * 0.8, 0.1))}
            style={{
              padding: "8px 12px",
              backgroundColor: "#ffffff",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
            title="Zoom out (-)"
          >
            -
          </button>
        </div>
      )}

      {/* Instructions - Toggleable */}
      {showControls && (
        <div
          style={{
            position: "absolute",
            bottom: 20,
            left: 10,
            zIndex: 1000,
            backgroundColor: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "12px",
            maxWidth: "300px",
          }}
        >
          <div>🖱️ Scroll for å zoome</div>
          <div>👆 Klikk og dra for å panorere</div>
          <div>📱 Klyp for å zoome på mobil</div>
          <div>⌨️ Trykk R for å tilbakestille, +/- for å zoome</div>
        </div>
      )}

      {/* Image Container */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          overflow: "hidden",
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          ref={imageRef}
          src={imageSrc}
          alt={imageAlt}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "top left",
            transition: isDragging ? "none" : "transform 0.1s ease-out",
            maxWidth: "none",
            maxHeight: "none",
            position: "absolute",
            top: 0,
            left: 0,
            display: "block",
          }}
          draggable={false}
        />
      </div>
    </div>
  );
};

export default InteractiveImageViewer;
